// Shapes the endpoint's rows for the cards: percentages, bar widths, tints, labels.

import { alertMeta, bySize } from './constants'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// '2026-08-05' -> '5 Aug', parsed by hand so the day cannot shift by timezone.
export const formatDayLabel = (iso) => {
  const [, month, day] = (iso || '').split('-')
  if (!month || !day) return iso || ''
  return `${Number(day)} ${MONTHS[Number(month) - 1] ?? ''}`.trim()
}

const initials = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return (words[0] ?? '').slice(0, 2).toUpperCase()
}

// Biggest first, which is also the legend order.
const mapBreakdown = (rows = []) =>
  bySize(rows.map((row) => ({ ...alertMeta(row.signal), count: row.count })))

// The chart wants a value per series per day in legend order.
const mapSeries = (rows = [], breakdown = []) =>
  rows.map((row) => ({
    label: formatDayLabel(row.date),
    values: breakdown.map((meta) => row.counts?.[meta.id] ?? 0),
  }))

const mapEmployees = (rows = []) =>
  rows.map((row) => ({
    profileId: row.profileId,
    name: row.name,
    dept: row.department,
    init: initials(row.name),
    count: row.count,
    vip: Boolean(row.vip),
    trend: row.trend,
  }))

// One response into the four blocks the page renders.
export const mapOverview = (response = {}) => {
  const breakdown = mapBreakdown(response.alertsBreakdown)
  return {
    kpi: response.kpis ?? null,
    days: response.timeframe?.days ?? 0,
    breakdown,
    series: mapSeries(response.alertsOverTime, breakdown),
    employees: mapEmployees(response.mostTargetedEmployees),
  }
}

// Design's tint thresholds, read off relative volume - there is no risk score.
const tintFor = (share) => {
  if (share >= 75) return 'high'
  if (share >= 50) return 'medium'
  return 'low'
}

// `pct` is the slice's share of the total, printed in the donut legend.
export const toSignals = (breakdown = []) => {
  const total = breakdown.reduce((sum, row) => sum + row.count, 0)
  return breakdown.map((row) => ({
    ...row,
    pct: total ? (row.count / total) * 100 : 0,
  }))
}

// `share` drives the row's bar, relative to the busiest employee listed.
export const toEmployees = (employees = []) => {
  const busiest = employees.reduce((max, row) => Math.max(max, row.count), 0)
  return employees.map((row) => {
    const share = busiest ? (row.count / busiest) * 100 : 0
    return { ...row, share, severity: tintFor(share) }
  })
}

// The KPI sparkline: the over-time series summed across categories.
export const dailyTotals = (series = []) =>
  series.map((day) => day.values.reduce((sum, value) => sum + value, 0))
